import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Container,
  List,
  Typography,
  Drawer,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  LinearProgress,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FolderIcon from "@mui/icons-material/Folder";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { styled } from "@mui/material/styles";
import { nextStep, updateTotalPages } from "../../redux/formSlice";
import {
  AddFileSanad,
  ListFileAddSanad,
  DeleteFileSanad,
  DeleteAllfileSanad,
} from "../../services/sanad/sanad";
import { Alert, Confirm } from "../../utils/alert";
import Swal from "sweetalert2";
import ListItemComponent from "../../components/Listitem";
import { AnimatePresence, motion } from "framer-motion";
import CropImage from "./CropImage"; // Import the CropImage component
import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.js?url";
import { useTranslation } from "react-i18next";
import StepNavigation from "../../components/StepNavigation";

// Set the worker
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const remainingSize = (maxUploadSize, usedFileSize) =>
  maxUploadSize - usedFileSize;

const FileUpload = () => {
  const { t } = useTranslation();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [error] = useState("");
  const dispatch = useDispatch();
  const [maxUploadSize, setMaxUploadSize] = useState(30);
  const [usedFileSize, setUsedFileSize] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const steps = [
    t("Upload.Documentlist"),
    t("Upload.Size"),
    t("Upload.Setting"),
    t("Upload.Setting"),
    t("Upload.Name"),
    t("Upload.End"),
  ];



  const fetchFiles = async () => {
    try {
      const res = await ListFileAddSanad();
      const fileData = res.data.value.listpathfile;
      const limitFileUpload = res.data.value.limitfileupload;
      const totalPage = res.data.value.totalPage;
      const limitUseFile = parseFloat(res.data.value.limituseFile);

      setUploadedFiles(fileData);
      setMaxUploadSize(limitFileUpload);
      setUsedFileSize(limitUseFile);
      dispatch(updateTotalPages(totalPage));

      // Don’t count pages here — it’s slower and unreliable after upload
    } catch (err) {
      console.error("Error fetching files:", err);
    }
  };
  useEffect(() => {
    fetchFiles();
  }, []);

  // Check total upload size
  const checkTotalSize = (newFiles) => {
    const existingFilesSize = uploadedFiles.reduce(
      (sum, file) => sum + parseFloat(file.size),
      0
    );
    const newFilesSize = newFiles.reduce(
      (sum, file) => sum + file.size / (1024 * 1024),
      0
    );
    const totalNewSize = existingFilesSize + newFilesSize;

    if (totalNewSize > maxUploadSize) {
      Swal.fire({
        icon: "",
        title: t("Progress.uploadfileT"),
        text: t("Progress.sizeerror"),
        confirmButtonText: t("Progress.ok"),
      });
      setDrawerOpen(false);
      return false;
    }
    return true;
  };

  const handleFileChange = async (e) => {
    const newFiles = Array.from(e.target.files);
    const allowedTypes = [".pdf", ".png", ".jpg", ".jpeg"];

    if (!checkTotalSize(newFiles)) return;

    for (const file of newFiles) {
      const fileExtension = file.name
        .slice(file.name.lastIndexOf("."))
        .toLowerCase();
      if (!allowedTypes.includes(fileExtension)) {
        Swal.fire({
          icon: "",
          title: t("Progress.formatT"),
          text: t("Progress.formatD"),
          confirmButtonText: t("Progress.ok"),
        });
        return;
      }
    }

    const formData = new FormData();
    newFiles.forEach((file) => {
      formData.append("listpathfile", file);
    });

    try {
      Swal.fire({
        title: t("Progress.Uploading"),
        html: `
        <div id="progress-bar" style="width: 100%; background: #f1f1f1; border-radius: 5px; height: 15px; margin-top: 10px;">
            <div id="progress-fill" style="height: 100%; width: 0; background-color: #4caf50; border-radius: 5px;"></div>
          </div>
          <p id="progress-percentage" style="margin-top: 10px;">0%</p>
        `,
        allowOutsideClick: false,
        showCancelButton: false,
        showConfirmButton: false,
      });
      setDrawerOpen(false);
      // Send API request
      const response = await AddFileSanad(formData, (percentage) => {
        document.getElementById("progress-fill").style.width = percentage + "%";
        document.getElementById("progress-percentage").innerText =
          percentage + "%";
      });

      // Check HTTP status code
      if (response.status === 200) {
        Swal.fire({
          icon: "success",
          title: t("Progress.Uploadsuccess"),
          showConfirmButton: false,
          timer: 1500,
        });
        await fetchFiles(); // This will now recount all pages including new files
      } else {
        throw new Error("بارگذاری ناموفق بود. لطفاً دوباره تلاش کنید.");
      }
    } catch (error) {
      document.getElementById("progress-fill").style.width = "0%";
      document.getElementById("progress-percentage").innerText = "0%";

      Swal.fire({
        icon: "error",
        title: t("Progress.Uploadfail"),
        text: error.message || t("Progress.tryagain"),
      });
    }
  };

  const handleImageUpload = (event) => {
    setDrawerOpen(false);

    const file = event.target.files[0];
    if (file) {
      setSelectedImage(URL.createObjectURL(file));
      setIsCropping(true);
    }
  };

  const handleCropComplete = (fileData) => {
    setUploadedFiles((prev) => [...prev, fileData]);
    setUsedFileSize((prev) => prev + parseFloat(fileData.size));
    setIsCropping(false);
    setSelectedImage(null);
  };

  const handleDelete = async (fileUrl) => {
    const fileName = fileUrl.split("/").pop(); // Extract file name from URL
    const { value } = await Confirm(
      t("Progress.DeleteT"),
      t("Progress.Delete")
    );
    if (value) {
      try {
        await DeleteFileSanad(fileName); // Delete file from server
        await fetchFiles(); // This will recount pages after deletion
      } catch (err) {
        Alert("خطا", error?.response?.data, "");
      }
    }
  };
  const handleDeleteAll = async () => {
    const { value } = await Confirm(
      t("Progress.DeleteT"),
      t("Progress.Deleteall"),
      ""
    );
    if (value) {
      try {
        await DeleteAllfileSanad();
        await fetchFiles(); // This will reset page count to 0
        Alert(t("Progress.DeleteT"), t("Progress.Deletesuccess"), "");
      } catch (error) {
        Alert(t("Progress.err"), error?.response?.data, "");
      }
    }
  };

  return (
    <Box>
      <Container
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 5,
          backgroundColor: "background.paper",
          marginBottom: "1.5rem",
          paddingLeft: "1rem",
          paddingRight: "1rem",
          paddingTop: "5rem",
          paddingBottom: "1rem",
          borderBottom: "2px solid #BAB3BD",
        }}
      >
        {/* Stepper replaced with LinearProgress and labels */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 4,
          }}
        >
          <span style={{ fontWeight: 500 }}>{steps[0]}</span>
        </div>
        <LinearProgress
          variant="determinate"
          value={0}
          sx={{ mb: "1.2rem", height: 8, borderRadius: 2 }}
        />
        <Button
          component="label"
          role={undefined}
          variant="outlined"
          color="primary"
          tabIndex={-1}
          style={{
            display: "flex",
            flexDirection: "column",
            border: "2px dashed",
            padding: ".95rem 0",
          }}
          onClick={() => setDrawerOpen(true)} // Open drawer on click
        >
          <CloudUploadIcon sx={{ fontSize: "60px", marginBottom: ".2rem" }} />
          <Typography
            sx={{ marginBottom: ".5rem", textTransform: "none" }}
            variant="h2"
          >
            {t("Upload.Upload")}
          </Typography>

          <Typography variant="h5" sx={{ textTransform: "none" }}>
            {t("Upload.Remain")}{" "}
            {remainingSize(maxUploadSize, usedFileSize).toFixed(2)}{" "}
            {t("Upload.Meg")}
          </Typography>
        </Button>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "18px",
          }}
        >
          <Typography variant="h2">{t("Upload.Documentlist")}</Typography>
          <Button color="error" onClick={handleDeleteAll}>
            {t("Upload.Deleteall")}
          </Button>
        </Box>
        {error && <p style={{ color: "red" }}>{error}</p>}
      </Container>
      <Box
        sx={{
          height: "calc(100vh - 14%)",
          paddingTop: "18rem",
          marginBottom: "1rem",
          overflow: "auto",
        }}
      >
        <List sx={{ marginBottom: "6rem" }}>
          <AnimatePresence>
            {uploadedFiles.map((file, index) => (
              <motion.div
                key={file.pathlist}
                initial={{ opacity: 0, x: 5 }} // Initial animation: fade in + slide from right
                animate={{ opacity: 1, x: 0 }} // Mount animation: fade in + slide to position
                exit={{ opacity: 0, x: -5 }} // Exit animation: fade out + slide to left
                transition={{
                  duration: 0.5,
                  delay: 0.1 * (index + 1), // Add a delay for each subsequent item based on its index
                  // Duration for enter animation
                  exit: { duration: 1.5, ease: "easeInOut" }, // Longer duration for exit animation
                }}
              >
                <ListItemComponent
                  fileSrc={file.pathlist}
                  fileName={index + 1}
                  fileSize={`${file.size} ${t("Upload.Meg")}`}
                  onRemove={() => handleDelete(file.pathlist)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </List>
      </Box>
      <Container
        sx={{
          position: "fixed",
          bottom: "14%",
          display: "flex",
          justifyContent: "end",
          width: {
            xs: "90%",
            sm: "90%",
            md: "84%",
            xl: "56%",
          },
          mx: "auto",
        }}
      >
        <StepNavigation
          onContinue={() => dispatch(nextStep())}
          continueDisabled={uploadedFiles.length === 0}
          showBack={false}
          continueLabel={t("Upload.Continue")}
          justifyEndIfSingle={true}
        />
      </Container>
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            padding: 2,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t("Upload.upload-file")}
          </Typography>
          <List sx={{ width: "100%", mt: 2 }}>
            <ListItemButton component="label">
              <VisuallyHiddenInput
                type="file"
                accept=".pdf, .png, .jpg, .jpeg, .tiff, .tif"
                multiple
                onChange={handleFileChange}
              />
              <ListItemIcon>
                <FolderIcon />
              </ListItemIcon>
              <ListItemText primary={t("Upload.Select")} />
            </ListItemButton>
            <ListItemButton component="label">
              <VisuallyHiddenInput
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleImageUpload}
              />
              <ListItemIcon>
                <PhotoCameraIcon />
              </ListItemIcon>
              <ListItemText primary={t("Upload.Camera")} />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>
      {isCropping && (
        <CropImage
          imageSrc={selectedImage}
          onCropComplete={handleCropComplete}
          onCancel={() => setIsCropping(false)}
          maxUploadSize={maxUploadSize}
          usedFileSize={usedFileSize}
        />
      )}
    </Box>
  );
};

export default FileUpload;
